import { useKindeAuth } from "@kinde-oss/kinde-auth-react";
import { useCallback, useEffect } from "react";
import { useNavigate, useParams } from "react-router-dom";
import { ability } from "@/casl";
import { defineAbilities } from "@/casl/ability";
import { AccountType } from "@/constants/accountTypes";
import { usePermissions } from "@/permissions";
import { useLazyGetAccountsQuery } from "@/redux/apis/account/accountApi";
import { useLazyGetOnboardingStatusQuery } from "@/redux/apis/onboarding/onboardingApi";
import { useLazyGetOperatorsQuery } from "@/redux/apis/operator/operatorApi";
import { useLazyGetProfileQuery } from "@/redux/apis/profile/profileApi";
import { useLazyGetProviderSubscriptionsQuery } from "@/redux/apis/subscription/subscriptionPlansApi";
import { useAppDispatch, useAppSelector } from "@/redux/hooks";
import { setActiveAccount, setSetupFinished } from "@/redux/slices/account/accountSlice";
import { switchOperator } from "@/redux/slices/operator/operatorSlice";
import { Operator } from "@/redux/slices/operator/types";
import { Profile } from "@/redux/slices/profile/types";

// Type definitions for Kinde claims
type UserProperty = { v: string };
type ClaimsWithUserProperties = {
  user_properties?: {
    kp_usr_utm_content?: UserProperty;
  };
};

type UseSetupAccount =
  | {
      isReady: false;
      operators?: Operator[];
      profile?: Profile;
    }
  | {
      isReady: true;
      operators: Operator[];
      profile: Profile;
    };

export const useSetupAccount = (): UseSetupAccount => {
  const dispatch = useAppDispatch();
  const { operatorId } = useParams();
  const isReady = useAppSelector((state) => state.account.setupFinished);
  const activeOperator = useAppSelector((state) => state.operator.activeOperator);
  const navigate = useNavigate();
  const { getClaims } = useKindeAuth();
  const { setPermissions } = usePermissions();

  const [getAccounts] = useLazyGetAccountsQuery();
  const [getOperators, { data: operators, isFetching: isFetchingOperators }] = useLazyGetOperatorsQuery();
  const [getProfile, { data: profile, isFetching: isFetchingProfile }] = useLazyGetProfileQuery();
  const [getOnboardingStatus] = useLazyGetOnboardingStatusQuery();
  const [getProviderSubscriptions] = useLazyGetProviderSubscriptionsQuery();

  const getProfileOperators = useCallback(
    () =>
      getOperators()
        .unwrap()
        .then((res) => {
          const profileOperators = res.data.operators;

          if (operatorId) {
            const operator = profileOperators.find((operator) => operatorId === operator.id);
            if (operator) {
              ability.update(defineAbilities(operator.permissions));
              setPermissions(operator.permissions);
              dispatch(switchOperator(operator));
              const isAdmin = operator.roles.includes("Fa_Access") || operator.roles.includes("Owner") || operator.roles.includes("Admin");
              if (isAdmin) {
                getOnboardingStatus();
              }
              // Load subscriptions upfront
              getProviderSubscriptions({
                providerId: operator.id,
                status: ["active", "pending_cancellation", "trial"],
                limit: 1,
                orderBy: ["startDate:DESC"],
              });
              dispatch(setSetupFinished(true));
            } else navigate("../login");
          } else {
            // Find primary operator, or fallback to first operator if no primary exists
            const defaultOperator = profileOperators.find((operator) => operator.primary) || profileOperators[0];
            if (!defaultOperator) throw new Error("Default operator not found: " + JSON.stringify(profileOperators));
            ability.update(defineAbilities(defaultOperator.permissions));
            setPermissions(defaultOperator.permissions);
            dispatch(switchOperator(defaultOperator));
            const isAdmin =
              defaultOperator.roles.includes("Fa_Access") ||
              defaultOperator.roles.includes("Owner") ||
              defaultOperator.roles.includes("Admin");
            if (isAdmin) {
              getOnboardingStatus();
            }
            // Load subscriptions upfront
            getProviderSubscriptions({
              providerId: defaultOperator.id,
              status: ["active", "pending_cancellation", "trial"],
              limit: 1,
              orderBy: ["startDate:DESC"],
            });
            dispatch(setSetupFinished(true));
          }
        })
        .catch(() => {
          console.error("Failed to fetch operators");

          // Check if user is a client type — profile is already fetched at this point
          const clientTypes: string[] = [AccountType.PASSENGER, AccountType.EXECUTIVE_ASSISTANT, AccountType.TRAVEL_MANAGER];
          const profileType = profile?.accountMetadata?.type;

          if (profileType && clientTypes.includes(profileType)) {
            console.warn("useSetupAccount - Client user attempting to access operator dashboard, redirecting to client portal");
            navigate("/client", { replace: true });
            return;
          }

          // Fallback: check Kinde claims for legacy "client" value
          getClaims("idToken")
            .then((claims) => {
              const typedClaims = claims as ClaimsWithUserProperties;
              const content = typedClaims?.user_properties?.kp_usr_utm_content?.v;

              if (content && (content === "client" || clientTypes.includes(content))) {
                navigate("/client", { replace: true });
              } else {
                navigate("/onboarding/operator");
              }
            })
            .catch(() => {
              console.error("Failed to fetch Kinde claims");
              navigate("/onboarding/operator");
            });
        }),
    [dispatch, navigate, getOperators, operatorId, getOnboardingStatus, getClaims, setPermissions, getProviderSubscriptions, profile?.accountMetadata?.type]
  );

  const assignOperator = useCallback(
    (id: string) => {
      if (!operators) return;
      const operator = operators.data.operators.find((i) => i.id === id);
      if (operator) {
        ability.update(defineAbilities(operator.permissions));
        setPermissions(operator.permissions);
        dispatch(switchOperator(operator));
        const isAdmin = operator.roles.includes("Fa_Access") || operator.roles.includes("Owner") || operator.roles.includes("Admin");
        if (isAdmin) {
          getOnboardingStatus();
        }
        // Load subscriptions upfront
        getProviderSubscriptions({
          providerId: operator.id,
          status: ["active", "pending_cancellation", "trial"],
          limit: 1,
          orderBy: ["startDate:DESC"],
        });
        dispatch(setSetupFinished(true));
      } else throw new Error(`Operator with id ${operatorId} not found`);
    },
    [dispatch, operatorId, operators, getOnboardingStatus, setPermissions, getProviderSubscriptions]
  );

  useEffect(() => {
    getAccounts()
      .unwrap()
      .then((res) => {
        const accounts = res.data.accounts;
        const primaryAccount = accounts.find((account) => account.primary);
        if (!primaryAccount) throw new Error("Default account not found: " + JSON.stringify(accounts));
        dispatch(setActiveAccount(primaryAccount));
        getProfile()
          .unwrap()
          .then((fetchedProfile) => {
            // If user is a client type, redirect to client portal — don't load operators
            const clientTypes: string[] = [AccountType.PASSENGER, AccountType.EXECUTIVE_ASSISTANT, AccountType.TRAVEL_MANAGER];
            const profileType = fetchedProfile.accountMetadata?.type;
            if (profileType && clientTypes.includes(profileType)) {
              navigate("/client", { replace: true });
              return;
            }
            getProfileOperators();
          })
          .catch(() => {
            console.error("Failed to fetch profile");
          });
      })
      .catch(() => {
        console.error("Failed to fetch accounts");
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (operatorId && activeOperator?.id !== operatorId) assignOperator(operatorId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [operatorId]);

  const isFetching = isFetchingOperators || isFetchingProfile;

  return {
    isReady: !isFetching && isReady,
    profile,
    operators: operators?.data.operators,
  } as UseSetupAccount;
};
